import { memo, useEffect, useRef, useState, type ReactNode } from 'react';

import { Reveal } from '../components/effects/Reveal';
import { Spotlight } from '../components/effects/Spotlight';
import { buttonVariants } from '../components/ui/Button';
import { subscribeToCases } from '../firebase/casesService';
import { useHorizontalCarousel } from '../hooks/useHorizontalCarousel';
import { captureException } from '../lib/monitoring';
import type { CaseStudy } from '../types/domain';

type Status = 'loading' | 'ready' | 'error';

/* ─── Constantes de layout ──────────────────────────────────────────────── */
const CARD_WIDTH = 360;
const CARD_GAP = 28;

/* ─── Ilustración SVG por índice ─────────────────────────────────────────── */
interface CaseIllustrationProps {
  index: number;
}

const CaseIllustration = memo(function CaseIllustration({ index }: CaseIllustrationProps) {
  const gradients = [
    'linear-gradient(135deg, #1003A3 0%, #1a1060 100%)',
    'linear-gradient(135deg, #1003A3 0%, #4c1d95 100%)',
    'linear-gradient(135deg, #1003A3 0%, #065f46 100%)',
    'linear-gradient(135deg, #1003A3 0%, #1e3a8a 100%)',
    'linear-gradient(135deg, #1003A3 0%, #831843 100%)',
    'linear-gradient(135deg, #1003A3 0%, #0369a1 100%)',
    'linear-gradient(135deg, #1003A3 0%, #1e293b 100%)',
    'linear-gradient(135deg, #1003A3 0%, #581c87 100%)',
    'linear-gradient(135deg, #1003A3 0%, #020617 100%)',
  ];

  const currentGradient = gradients[index % gradients.length];

  return (
    <div
      className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden transition-transform duration-700 group-hover:scale-110"
      style={{ background: currentGradient }}
    >
      <svg className="w-full h-full opacity-70" viewBox="0 0 320 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="yellowGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFC836" />
            <stop offset="100%" stopColor="#FFD870" />
          </linearGradient>
          <linearGradient id="blueGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#327FEF" />
            <stop offset="100%" stopColor="#A8C7FE" />
          </linearGradient>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {index % 9 === 0 && (
          <>
            <path d="M40 150 L160 80 L280 150 M160 80 V190" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            <path d="M40 100 L160 30 L280 100" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            <path d="M80 130 L160 90 L240 130 L160 170 Z" stroke="url(#blueGrad)" strokeWidth="1.5" strokeDasharray="4 4" />
            <g transform="translate(160, 90)">
              <polygon points="0,-12 14,-5 0,2 -14,-5" fill="url(#yellowGrad)" />
              <polygon points="-14,-5 0,2 0,12 -14,5" fill="#E6AC18" />
              <polygon points="0,2 14,-5 14,5 0,12" fill="#FFC836" />
            </g>
            <g transform="translate(80, 130)">
              <polygon points="0,-8 10,-3 0,2 -10,-3" fill="rgba(255,255,255,0.8)" />
              <polygon points="-10,-3 0,2 0,9 -10,4" fill="rgba(255,255,255,0.6)" />
              <polygon points="0,2 10,-3 10,4 0,9" fill="rgba(255,255,255,0.7)" />
            </g>
            <g transform="translate(240, 130)">
              <polygon points="0,-8 10,-3 0,2 -10,-3" fill="rgba(255,255,255,0.8)" />
              <polygon points="-10,-3 0,2 0,9 -10,4" fill="rgba(255,255,255,0.6)" />
              <polygon points="0,2 10,-3 10,4 0,9" fill="rgba(255,255,255,0.7)" />
            </g>
            <g transform="translate(160, 170)">
              <polygon points="0,-8 10,-3 0,2 -10,-3" fill="rgba(255,255,255,0.8)" />
              <polygon points="-10,-3 0,2 0,9 -10,4" fill="rgba(255,255,255,0.6)" />
              <polygon points="0,2 10,-3 10,4 0,9" fill="rgba(255,255,255,0.7)" />
            </g>
          </>
        )}

        {index % 9 === 1 && (
          <>
            <circle cx="160" cy="100" r="60" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx="160" cy="100" r="45" stroke="url(#blueGrad)" strokeWidth="1.5" />
            <path d="M140 70 H180 V90 C180 115 160 130 160 130 C160 130 140 115 140 90 Z" fill="rgba(255,255,255,0.08)" stroke="url(#yellowGrad)" strokeWidth="2" filter="url(#glow)" />
            <path d="M160 82 L163 93 L174 96 L163 99 L160 110 L157 99 L146 96 L157 93 Z" fill="url(#yellowGrad)" />
            <path d="M60 150 Q110 120 160 150 T260 150" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
            <path d="M80 165 Q120 140 160 165 T240 165" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          </>
        )}

        {index % 9 === 2 && (
          <>
            <line x1="40" y1="70" x2="280" y2="70" stroke="rgba(255,255,255,0.1)" strokeWidth="6" strokeLinecap="round" />
            <line x1="40" y1="100" x2="280" y2="100" stroke="url(#blueGrad)" strokeWidth="6" strokeLinecap="round" />
            <line x1="40" y1="130" x2="280" y2="130" stroke="rgba(255,255,255,0.1)" strokeWidth="6" strokeLinecap="round" />
            <circle cx="100" cy="70" r="4" fill="#FFC836" filter="url(#glow)" />
            <circle cx="220" cy="70" r="4" fill="#FFC836" />
            <circle cx="70" cy="100" r="5" fill="#fff" filter="url(#glow)" />
            <circle cx="160" cy="100" r="5" fill="#FFC836" filter="url(#glow)" />
            <circle cx="250" cy="100" r="5" fill="#fff" />
            <circle cx="130" cy="130" r="4" fill="#327FEF" />
            <circle cx="190" cy="130" r="4" fill="#327FEF" filter="url(#glow)" />
            <path d="M260 90 L275 100 L260 110" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </>
        )}

        {index % 9 === 3 && (
          <>
            <rect x="60" y="50" width="200" height="100" rx="6" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
            <line x1="60" y1="70" x2="260" y2="70" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
            <circle cx="72" cy="60" r="3" fill="#ef4444" />
            <circle cx="82" cy="60" r="3" fill="#f59e0b" />
            <circle cx="92" cy="60" r="3" fill="#22c55e" />
            <path d="M90 130 L120 100 L150 115 L180 85 L210 95 L230 75" stroke="url(#yellowGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow)" />
            <circle cx="180" cy="85" r="4" fill="#fff" />
            <circle cx="230" cy="75" r="4" fill="#FFC836" />
            <rect x="90" y="110" width="12" height="20" rx="2" fill="url(#blueGrad)" opacity="0.6" />
            <rect x="120" y="95" width="12" height="35" rx="2" fill="url(#blueGrad)" opacity="0.6" />
            <rect x="150" y="105" width="12" height="25" rx="2" fill="url(#blueGrad)" opacity="0.6" />
            <rect x="210" y="85" width="12" height="45" rx="2" fill="url(#blueGrad)" opacity="0.8" />
          </>
        )}

        {index % 9 === 4 && (
          <>
            <path d="M40 100 H110 L120 70 L130 130 L140 100 L150 110 L160 50 L170 150 L180 100 L195 85 L205 115 L215 100 H280" stroke="url(#blueGrad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M40 100 H110 L120 70 L130 130 L140 100 L150 110 L160 50 L170 150 L180 100 L195 85 L205 115 L215 100 H280" stroke="url(#yellowGrad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" filter="url(#glow)" />
            <g transform="translate(160, 50)">
              <circle cx="0" cy="0" r="12" fill="url(#yellowGrad)" filter="url(#glow)" opacity="0.3" />
              <polygon points="0,-8 2,-2 8,0 2,2 0,8 -2,2 -8,0 -2,-2" fill="#FFC836" />
            </g>
            <circle cx="170" cy="150" r="4" fill="#327FEF" filter="url(#glow)" />
          </>
        )}

        {index % 9 === 5 && (
          <>
            <circle cx="160" cy="100" r="70" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <circle cx="160" cy="100" r="40" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <circle cx="90" cy="100" r="5" fill="#327FEF" />
            <circle cx="230" cy="100" r="5" fill="#327FEF" />
            <circle cx="160" cy="40" r="5" fill="#FFC836" />
            <circle cx="160" cy="160" r="5" fill="#FFC836" />
            <path d="M90 100 Q160 20 230 100" stroke="url(#yellowGrad)" strokeWidth="1.5" strokeDasharray="3 3" />
            <path d="M90 100 Q160 180 230 100" stroke="url(#blueGrad)" strokeWidth="1.5" />
            <path d="M160 40 Q250 100 160 160" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
            <path d="M160 40 Q70 100 160 160" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
            <g transform="translate(160, 60) rotate(45)">
              <polygon points="0,-6 2,0 8,2 2,3 0,9 -2,3 -8,2 -2,0" fill="#fff" />
            </g>
          </>
        )}

        {index % 9 === 6 && (
          <>
            <rect x="70" y="45" width="180" height="110" rx="8" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
            <circle cx="120" cy="100" r="28" stroke="rgba(255,255,255,0.1)" strokeWidth="6" fill="none" />
            <circle cx="120" cy="100" r="28" stroke="url(#yellowGrad)" strokeWidth="6" fill="none" strokeDasharray="130 180" strokeDashoffset="20" filter="url(#glow)" />
            <circle cx="120" cy="100" r="28" stroke="url(#blueGrad)" strokeWidth="6" fill="none" strokeDasharray="40 180" strokeDashoffset="150" />
            <line x1="180" y1="80" x2="225" y2="80" stroke="rgba(255,255,255,0.2)" strokeWidth="4" strokeLinecap="round" />
            <line x1="180" y1="80" x2="215" y2="80" stroke="#FFC836" strokeWidth="4" strokeLinecap="round" />
            <line x1="180" y1="95" x2="225" y2="95" stroke="rgba(255,255,255,0.2)" strokeWidth="4" strokeLinecap="round" />
            <line x1="180" y1="95" x2="200" y2="95" stroke="#327FEF" strokeWidth="4" strokeLinecap="round" />
            <line x1="180" y1="110" x2="225" y2="110" stroke="rgba(255,255,255,0.2)" strokeWidth="4" strokeLinecap="round" />
            <line x1="180" y1="110" x2="220" y2="110" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
          </>
        )}

        {index % 9 === 7 && (
          <>
            <circle cx="160" cy="100" r="22" fill="rgba(255,255,255,0.06)" stroke="url(#yellowGrad)" strokeWidth="1.5" filter="url(#glow)" />
            <circle cx="160" cy="100" r="8" fill="#FFC836" />
            <g transform="translate(90, 60)">
              <circle cx="0" cy="0" r="14" fill="rgba(255,255,255,0.05)" stroke="url(#blueGrad)" strokeWidth="1" />
              <rect x="-4" y="-4" width="8" height="8" rx="1" fill="#327FEF" />
            </g>
            <g transform="translate(230, 60)">
              <circle cx="0" cy="0" r="14" fill="rgba(255,255,255,0.05)" stroke="url(#blueGrad)" strokeWidth="1" />
              <rect x="-4" y="-4" width="8" height="8" rx="1" fill="#327FEF" />
            </g>
            <g transform="translate(90, 140)">
              <circle cx="0" cy="0" r="14" fill="rgba(255,255,255,0.05)" stroke="url(#blueGrad)" strokeWidth="1" />
              <rect x="-4" y="-4" width="8" height="8" rx="1" fill="#327FEF" />
            </g>
            <g transform="translate(230, 140)">
              <circle cx="0" cy="0" r="14" fill="rgba(255,255,255,0.05)" stroke="url(#blueGrad)" strokeWidth="1" />
              <rect x="-4" y="-4" width="8" height="8" rx="1" fill="#327FEF" />
            </g>
            <line x1="160" y1="100" x2="90" y2="60" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
            <line x1="160" y1="100" x2="230" y2="60" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
            <line x1="160" y1="100" x2="90" y2="140" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
            <line x1="160" y1="100" x2="230" y2="140" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
            <path d="M90 60 H230 V140 H90 Z" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="3 3" />
          </>
        )}

        {index % 9 === 8 && (
          <>
            <g transform="translate(100, 100)">
              <rect x="-25" y="-15" width="40" height="30" rx="4" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
              <line x1="-15" y1="-5" x2="5" y2="-5" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
              <line x1="-15" y1="5" x2="0" y2="5" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
            </g>
            <g transform="translate(110, 110)">
              <rect x="-25" y="-15" width="40" height="30" rx="4" fill="rgba(255,255,255,0.1)" stroke="url(#blueGrad)" strokeWidth="1.5" />
              <line x1="-15" y1="-5" x2="5" y2="-5" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
              <line x1="-15" y1="5" x2="0" y2="5" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
            </g>
            <path d="M125 110 Q160 110 185 85" stroke="url(#yellowGrad)" strokeWidth="1.5" strokeDasharray="3 3" />
            <g transform="translate(205, 80)">
              <rect x="-25" y="-20" width="50" height="32" rx="6" fill="url(#blueGrad)" filter="url(#glow)" />
              <path d="M-5 12 L0 18 L5 12" fill="#327FEF" />
              <circle cx="-10" cy="-4" r="2" fill="#fff" />
              <circle cx="10" cy="-4" r="2" fill="#fff" />
              <path d="M-4 6 Q0 9 4 6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
            </g>
            <polygon points="165,65 167,71 173,73 167,75 165,81 163,75 157,73 163,71" fill="#FFC836" filter="url(#glow)" />
          </>
        )}
      </svg>
    </div>
  );
});

/* ─── Tarjeta de caso ────────────────────────────────────────────────────── */
interface CaseCardProps {
  c: CaseStudy;
  onOpen: (c: CaseStudy) => void;
}

const CaseCard = memo(function CaseCard({ c, onOpen }: CaseCardProps) {
  return (
    <div className="flex-shrink-0 h-full" style={{ width: `${CARD_WIDTH}px` }}>
      <Spotlight
        className="bg-white rounded-[14px] overflow-hidden flex flex-col h-full border border-etribe-line hover:-translate-y-1 hover:shadow-card-lg transition-all duration-300 group"
        color="rgba(16, 3, 163, 0.10)"
        size={520}
      >
          <button
          type="button"
          onClick={() => onOpen(c)}
          className="flex flex-col h-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-etribe-blue rounded-[14px]"
          data-cursor="grow"
          aria-label={`Ver detalle del caso: ${c.title}`}
        >
        <div className="aspect-[16/10] bg-etribe-blue relative overflow-hidden flex items-center justify-center">
          <CaseIllustration index={c.illustrationIndex} />
          <span className="absolute top-4 left-4 text-[11px] font-bold tracking-[0.12em] uppercase px-3 py-1.5 rounded-full bg-white text-etribe-blue z-10">
            {c.badge}
          </span>
          <span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.08em] uppercase text-white/90 bg-black/30 backdrop-blur px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            Ver detalle
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </div>
        <div className="p-7 flex-1 flex flex-col">
          <p className="text-[13px] text-etribe-gray-500 mb-2">{c.client}</p>
          <h3 className="text-[19px] leading-[1.2] mb-3">{c.title}</h3>
          <p className="font-heading text-[24px] text-etribe-blue leading-[1.15] mb-5 tracking-[0.005em]">
            {c.result}
          </p>
          {c.stack.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-auto">
              {c.stack.map((s) => (
                <span key={s} className="text-[11px] px-3 py-1.5 rounded-full bg-etribe-gray-bg text-etribe-ink-2">
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
        </button>
      </Spotlight>
    </div>
  );
});

/* ─── Modal de detalle ───────────────────────────────────────────────────── */
interface CaseModalProps {
  caseData: CaseStudy | null;
  onClose: () => void;
}

type DetailKind = 'challenge' | 'solution' | 'impact';

const DETAIL_META: Record<
  DetailKind,
  { label: string; accent: string; ring: string; iconBg: string; iconText: string; icon: ReactNode }
> = {
  challenge: {
    label: 'El reto',
    accent: 'before:bg-etribe-yellow',
    ring: 'ring-etribe-yellow/30',
    iconBg: 'bg-etribe-yellow/15',
    iconText: 'text-[#B27F00]',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0Z" />
      </svg>
    ),
  },
  solution: {
    label: 'La solución',
    accent: 'before:bg-etribe-blue-light',
    ring: 'ring-etribe-blue-light/30',
    iconBg: 'bg-etribe-blue-light/15',
    iconText: 'text-etribe-blue-light',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18h6" />
        <path d="M10 22h4" />
        <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
      </svg>
    ),
  },
  impact: {
    label: 'El impacto',
    accent: 'before:bg-etribe-green',
    ring: 'ring-etribe-green/30',
    iconBg: 'bg-etribe-green/15',
    iconText: 'text-etribe-green',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17l6-6 4 4 8-8" />
        <path d="M14 7h7v7" />
      </svg>
    ),
  },
};

interface DetailCardProps { kind: DetailKind; text: string }

function DetailCard({ kind, text }: DetailCardProps) {
  const meta = DETAIL_META[kind];
  return (
    <div
      className={[
        'relative bg-white rounded-[14px] p-6 border border-etribe-line ring-1 ring-transparent transition-all duration-300 hover:-translate-y-0.5 hover:shadow-card-lg',
        'before:absolute before:top-5 before:bottom-5 before:left-0 before:w-[3px] before:rounded-r-full',
        meta.accent,
        meta.ring,
      ].join(' ')}
    >
      <div className="flex items-center gap-3 mb-3">
        <span className={['w-9 h-9 rounded-[10px] grid place-items-center', meta.iconBg, meta.iconText].join(' ')}>
          {meta.icon}
        </span>
        <p className="font-heading text-[11px] tracking-[0.18em] uppercase text-etribe-ink font-bold">
          {meta.label}
        </p>
      </div>
      <p className="text-[14px] text-etribe-ink-2 leading-[1.6] text-justify hyphens-auto">{text}</p>
    </div>
  );
}

function CaseModal({ caseData, onClose }: CaseModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!caseData) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Focus inicial al botón de cierre.
    queueMicrotask(() => closeBtnRef.current?.focus());

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Focus trap: ciclar Tab dentro del panel.
      if (e.key === 'Tab' && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [caseData, onClose]);

  if (!caseData) return null;

  const hasDetails = caseData.challenge || caseData.solution || caseData.impact;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-modal-title"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
    >
      {/* Backdrop (no es focusable; el cierre por click sigue funcionando) */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-etribe-blue/40 backdrop-blur-md animate-[fadeIn_.25s_ease-out] cursor-pointer"
        style={{ animationFillMode: 'both' }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="relative bg-white w-full max-w-[1040px] max-h-[94vh] sm:max-h-[90vh] overflow-hidden shadow-[0_30px_80px_-20px_rgba(16,3,163,.45)] flex flex-col rounded-t-[24px] sm:rounded-[20px]"
        style={{
          animation: 'caseModalIn .35s cubic-bezier(.2,.8,.25,1)',
          animationFillMode: 'both',
        }}
      >
        {/* Hero visual compacto */}
        <div className="relative isolate overflow-hidden flex-shrink-0 h-[120px] sm:h-[140px]">
          <CaseIllustration index={caseData.illustrationIndex} />
          {/* Gradient overlay sutil */}
          <div
            aria-hidden
            className="absolute inset-0 z-[1]"
            style={{
              background:
                'linear-gradient(180deg, rgba(16,3,163,.2) 0%, rgba(16,3,163,.45) 100%)',
            }}
          />
          {/* Grid pattern decorativo */}
          <div
            aria-hidden
            className="absolute inset-0 z-[1] opacity-25 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />

          {/* Badge */}
          <span className="absolute top-4 left-4 sm:top-5 sm:left-7 z-10 inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] uppercase px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-etribe-blue shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-etribe-yellow" />
            {caseData.badge}
          </span>

          {/* Close button */}
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Cerrar caso"
            className="absolute top-3 right-3 sm:top-4 sm:right-5 z-10 w-10 h-10 rounded-full bg-white/95 backdrop-blur text-etribe-blue flex items-center justify-center hover:bg-white hover:rotate-90 hover:scale-105 transition-all duration-300 shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-etribe-blue focus-visible:ring-offset-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {/* Cuerpo scrolleable */}
        <div className="overflow-y-auto">
          {/* Título + cliente */}
          <div className="px-6 sm:px-10 pt-7 sm:pt-8">
            <p className="text-etribe-gray-500 text-[12px] sm:text-[13px] tracking-[0.04em] mb-2">
              {caseData.client}
            </p>
            <h3
              id="case-modal-title"
              className="font-heading text-etribe-ink text-[clamp(20px,2.4vw,28px)] leading-[1.15] tracking-[0.005em] max-w-[40ch]"
            >
              {caseData.title}
            </h3>
          </div>

          {/* Stack */}
          {caseData.stack.length > 0 && (
            <div className="px-6 sm:px-10 pt-8">
              <p className="eyebrow mb-3"><span>Stack</span></p>
              <div className="flex flex-wrap gap-2">
                {caseData.stack.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full bg-etribe-gray-bg text-etribe-ink-2 border border-etribe-line"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-etribe-blue/70" />
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Detalles Reto / Solución / Impacto */}
          {hasDetails && (
            <div className="px-6 sm:px-10 pt-8">
              <div className="grid md:grid-cols-3 gap-4">
                {caseData.challenge && <DetailCard kind="challenge" text={caseData.challenge} />}
                {caseData.solution && <DetailCard kind="solution" text={caseData.solution} />}
                {caseData.impact && <DetailCard kind="impact" text={caseData.impact} />}
              </div>
            </div>
          )}

          {/* Highlights */}
          {caseData.highlights.length > 0 && (
            <div className="px-6 sm:px-10 pt-8">
              <p className="eyebrow mb-4"><span>Highlights</span></p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {caseData.highlights.map((h) => (
                  <li key={h} className="flex gap-3 text-[14px] text-etribe-ink leading-[1.5]">
                    <span
                      aria-hidden
                      className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-etribe-green/12 text-etribe-green grid place-items-center"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer CTA */}
          <div className="mt-10 px-6 sm:px-10 py-6 bg-etribe-gray-bg border-t border-etribe-line flex flex-wrap items-center justify-between gap-4">
            <p className="text-[13px] text-etribe-ink-2 max-w-[40ch]">
              ¿Tu equipo enfrenta un reto similar? Hablémoslo.
            </p>
            <a
              href="#contacto"
              onClick={onClose}
              className="inline-flex items-center gap-2 font-heading text-[13px] tracking-[0.04em] uppercase font-bold text-white bg-etribe-blue px-5 py-3 rounded-full hover:bg-etribe-blue/90 transition-all hover:gap-3"
            >
              Conversemos
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Sección Cases ──────────────────────────────────────────────────────── */
export default function Cases() {
  const [cases, setCases] = useState<CaseStudy[]>([]);
  const [status, setStatus] = useState<Status>('loading');
  const [selected, setSelected] = useState<CaseStudy | null>(null);

  const {
    trackRef,
    canPrev,
    canNext,
    onScroll,
    scrollByStep,
    scrollToIndex,
    isDotActive,
  } = useHorizontalCarousel({
    cardWidth: CARD_WIDTH,
    cardGap: CARD_GAP,
    resetKey: cases.length,
  });

  useEffect(() => {
    const unsubscribe = subscribeToCases((data, err) => {
      if (err) {
        captureException(err, { section: 'cases', source: 'subscribeToCases' });
        setCases([]);
        setStatus('error');
        return;
      }
      setCases(data);
      setStatus('ready');
    });
    return () => unsubscribe();
  }, []);

  if (status !== 'ready' || cases.length === 0) return null;

  return (
    <section id="casos" className="bg-etribe-gray-bg py-[120px]">
      <div className="container-etribe">
        <div className="flex items-end justify-between mb-16 gap-6 flex-wrap">
          <div className="max-w-[720px]">
            <p className="eyebrow"><span>Resultados</span></p>
            <h2 className="text-[clamp(32px,3.6vw,48px)] mb-4">Historias que hablan por nosotros</h2>
            <p className="text-[clamp(17px,1.3vw,20px)] text-etribe-ink-2 leading-[1.55]">
              {cases.length} trabajos recientes con líderes Tier 1. Toca cualquier caso para ver el reto, la solución y el impacto.
            </p>
          </div>

          {(canPrev || canNext) && (
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => scrollByStep(-1)}
                disabled={!canPrev}
                aria-label="Caso anterior"
                className="w-11 h-11 rounded-full border border-etribe-line bg-white flex items-center justify-center text-etribe-blue transition-all duration-200 hover:bg-etribe-blue hover:text-white hover:border-etribe-blue disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-etribe-blue disabled:hover:border-etribe-line"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M19 12H5M11 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={() => scrollByStep(1)}
                disabled={!canNext}
                aria-label="Siguiente caso"
                className="w-11 h-11 rounded-full border border-etribe-line bg-white flex items-center justify-center text-etribe-blue transition-all duration-200 hover:bg-etribe-blue hover:text-white hover:border-etribe-blue disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-etribe-blue disabled:hover:border-etribe-line"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div
          ref={trackRef}
          onScroll={onScroll}
          className="cases-carousel-track flex overflow-x-auto pb-6 mb-4 -mx-2 px-2"
          style={{
            gap: `${CARD_GAP}px`,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
          aria-label="Lista de casos de éxito"
        >
          {cases.map((c, idx) => (
            <Reveal key={c.id} delay={(idx % 3) * 90} className="h-auto flex">
              <CaseCard c={c} onOpen={setSelected} />
            </Reveal>
          ))}
        </div>

        {cases.length > 1 && (
          <div className="flex items-center justify-center gap-2 mb-12">
            {cases.map((c, i) => {
              const isActive = isDotActive(i);
              return (
                <button
                  key={c.id}
                  onClick={() => scrollToIndex(i)}
                  aria-label={`Ir al caso ${c.title}`}
                  aria-current={isActive ? 'true' : undefined}
                  className={`rounded-full transition-all duration-300 ${
                    isActive ? 'w-6 h-2 bg-etribe-blue' : 'w-2 h-2 bg-etribe-line hover:bg-etribe-blue/40'
                  }`}
                />
              );
            })}
          </div>
        )}

        <div className="text-center">
          <a href="#contacto" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
            Quiero conocer más
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
        </div>
      </div>

      <CaseModal caseData={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
